import {assertAndContinue} from 'Asserts';
import {
  FetchOutcome,
  getAutobatcher,
  ModelFetcher,
  requestFetch,
  requestFetchOptions,
} from 'models/Autobatcher';
import {InaccessibleModelSelector} from 'models/reduceInaccessibleModels';
import {getLoadingState, LoadingState} from 'models/reduceLoadingState';
import nextReduxAction from 'redux-stores/nextReduxAction';
import {BenchlingStore} from 'redux-stores/Store';
import {expectNotImporting} from 'util/ImportTracker';
import * as net from 'util/Net';
import {INACCESSIBLE} from 'util/Types';

import {loadingCompleted, loadingFailed, loadingStarted} from '../LoadingStateReduxActions';
import {ManagedModelType, ManagedModelTypes} from '../ManagedModelDefinitions';

export const GET_MODEL_TIMEOUT_MS = 120000;

/**
 * Lazy-import the redux store to break the import-time dependency from ModelManager to the store (which in
 * turn imports many other things).
 */
function getStore(): BenchlingStore {
  const StoreModule = require('redux-stores/Store') as typeof import('redux-stores/Store');
  return StoreModule.getStore();
}

type Model<T extends ManagedModelType> = ManagedModelTypes[T] | INACCESSIBLE;

/**
 * A ModelFetcher that supports getOrFetchRequired()
 *
 * This works in conjunction with the Autobatcher, which handles network-level request deduplication. The
 * division of responsibilities is:
 * - Autobatcher: Ensures only one network request per model key per batch
 * - ModelManager: Manages model state in the store (loading/error states and model data) and ensures
 *   multiple callers share the same Promise to prevent duplicate loading states and timeout errors
 */
export abstract class ModelManager<T extends ManagedModelType> implements ModelFetcher {
  modelType: T;

  /**
   * Promise cache for in-flight model fetching requests. This provides application-level promise sharing to
   * prevent duplicate timeout errors when multiple call sites request the same model simultaneously.
   * The cache key includes both the model key and relevant fetch options to ensure correct promise sharing.
   */
  private pendingPromises = new Map<string, Promise<Model<T>>>();

  constructor(modelType: T) {
    expectNotImporting();
    this.modelType = modelType;
  }

  isKeyLoadedOrLoading(key: string): boolean {
    const isModelInStore = Boolean(this.getModel(key));
    const loadingState = getStore().getState().models.loadingState;
    const isLoading = loadingState[this.modelType] && Boolean(loadingState[this.modelType][key]);
    return isModelInStore || isLoading;
  }

  async fetchMulti(keys: Array<string>): Promise<void> {
    try {
      getStore().dispatch(loadingStarted(this.modelType, keys));
      await this.doFetchMulti(keys);
      getStore().dispatch(loadingCompleted(this.modelType, keys));
      const unfetchedKeys = keys.filter(
        (key) => this.getModel(key) == null && !this.isModelInaccessible(key)
      );
      assertAndContinue(
        unfetchedKeys.length === 0,
        `Expected all the models to exist for ${this.modelType} after fetching is completed.`,
        {unfetchedKeys}
      );
    } catch (e) {
      if (net.isServerRequestError(e)) {
        getStore().dispatch(loadingFailed(this.modelType, keys));
      }
      throw e;
    }
  }

  abstract doFetchMulti(keys: Array<string>): Promise<void>;

  abstract getModel(modelKey: string): ManagedModelTypes[T] | null;

  /**
   * Model managers can specify where a given model key is inaccessible. This supports model managers whose
   * inaccesible state is not in models.inaccessibleState (e.g. models.inaccessibleFileIds), though ideally
   * all inaccessible state would live together.
   */
  isModelInaccessible(modelKey: string): boolean {
    return (
      new InaccessibleModelSelector(getStore().getState()).getInaccessibleInfo(this.modelType, modelKey) !=
      null
    );
  }

  /**
   * Either fetches a model from the store and returns it (issuing a network request to load it if necessary),
   * or returns INACCESSIBLE if the user does not have access to the model.
   */
  fetchModelMaybeInaccessible(
    modelKey: string,
    fetchOptions: requestFetchOptions = {forceFetch: false}
  ): Promise<Model<T>> {
    const cacheKey = `${modelKey}:${JSON.stringify(fetchOptions)}`;
    const existingPromise = this.pendingPromises.get(cacheKey);
    if (existingPromise) {
      return existingPromise;
    }

    // Request the model through the autobatcher (handles network-level deduplication)
    requestFetch(this, modelKey, fetchOptions);

    const newPromise = this.waitForModelToLoad(modelKey, fetchOptions);

    const promiseWithCleanup = newPromise.finally(() => {
      this.pendingPromises.delete(cacheKey);
    });
    this.pendingPromises.set(cacheKey, promiseWithCleanup);
    return promiseWithCleanup;
  }

  /**
   * Fetches a model and returns it, throwing an exception if the user does not have access to the model.
   *
   * This serves the same purpose as DataConnector.getOrFetchRequired, but for cases where we want to
   * fetch a model within an async function rather than in the more specific, limited
   * context of a data connector.
   */
  async fetchModel(key: string, fetchOptions?: requestFetchOptions): Promise<ManagedModelTypes[T]> {
    const model = await this.fetchModelMaybeInaccessible(key, fetchOptions);
    if (model === INACCESSIBLE) {
      throw new Error(`User does not have access to model ${this.modelType} with key ${key}`);
    }
    return model;
  }

  /**
   * Fetches multiple models and returns them as an array.
   */
  async fetchModelList(
    keys: Array<string>,
    fetchOptions?: requestFetchOptions
  ): Promise<Array<ManagedModelTypes[T]>> {
    return await Promise.all(keys.map((k) => this.fetchModel(k, fetchOptions)));
  }

  /**
   * Waits for a model to load, handling timeouts and error states.
   *
   * While an autobatched fetch covering the key is pending or in flight, this waits for that fetch to settle
   * rather than enforcing GET_MODEL_TIMEOUT_MS: the fetch has its own failure handling, and wall-clock time
   * keeps passing while the browser throttles or freezes timers in background tabs. The timeout only applies
   * to time spent waiting with no fetch in flight for the key.
   *
   * Unforced callers return as soon as their model reaches the store, since a batched fetch can still mark
   * every key in the batch FAILED after some of them loaded. Forced callers wait for the fetch to settle
   * because the store may hold a stale copy of the model.
   */
  private async waitForModelToLoad(key: string, fetchOptions?: requestFetchOptions): Promise<Model<T>> {
    const forceFetch = Boolean(fetchOptions?.forceFetch);
    // A forced refetch only supersedes an inaccessible marker if it replaces this cached copy.
    const modelBeforeRefetch = forceFetch ? this.getModel(key) : null;
    if (!forceFetch) {
      const storedModel = this.getStoredModel(key);
      if (storedModel) {
        return storedModel;
      }
    }

    let idleSince = Number(new Date());
    for (;;) {
      const fetchOutcome = getAutobatcher().getFetchOutcome(this, key);
      if (fetchOutcome) {
        const settled = forceFetch
          ? {outcome: await fetchOutcome}
          : await this.waitForFetchOrStoredModel(key, fetchOutcome);
        if ('storedModel' in settled) {
          return settled.storedModel;
        }
        if (!forceFetch) {
          const storedModel = this.getStoredModel(key);
          if (storedModel) {
            return storedModel;
          }
        }
        const loadedModel = this.getLoadedModel(key, modelBeforeRefetch);
        // A failed forced refetch surfaces its error rather than the stale copy it was meant to replace.
        if (settled.outcome.failed && (!loadedModel || loadedModel === modelBeforeRefetch)) {
          throw settled.outcome.error;
        }
        if (loadedModel) {
          return loadedModel;
        }
        idleSince = Number(new Date());
        continue;
      }

      const loadedModel = this.getLoadedModel(key, modelBeforeRefetch);
      if (loadedModel) {
        return loadedModel;
      }
      const remainingMs = GET_MODEL_TIMEOUT_MS - (Number(new Date()) - idleSince);
      if (remainingMs <= 0) {
        // The model manager does not handle timeouts (data connectors will keep loading indefinitely).
        throw new Error(
          `Timeout of ${GET_MODEL_TIMEOUT_MS} ms exceeded in fetching model type ${this.modelType} with key ${key}`
        );
      }
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      await Promise.race([
        nextReduxAction(),
        new Promise<void>((resolve) => {
          timeoutId = setTimeout(resolve, remainingMs);
        }),
      ]);
      clearTimeout(timeoutId);
    }
  }

  /**
   * Resolves with the fetch outcome once the fetch settles, or earlier with the model once a store update
   * makes it available.
   */
  private waitForFetchOrStoredModel(
    key: string,
    fetchOutcome: Promise<FetchOutcome>
  ): Promise<{storedModel: Model<T>} | {outcome: FetchOutcome}> {
    return new Promise((resolve) => {
      const unsubscribe = getStore().subscribe(() => {
        const storedModel = this.getStoredModel(key);
        if (storedModel) {
          unsubscribe();
          resolve({storedModel});
        }
      });
      fetchOutcome.then((outcome) => {
        unsubscribe();
        resolve({outcome});
      });
    });
  }

  /**
   * Returns the model or INACCESSIBLE if it has loaded, or null if it has not. Throws if loading failed.
   *
   * An inaccessible marker takes precedence over modelBeforeRefetch, the copy cached before a forced refetch,
   * so a refetch that finds the model inaccessible is honored. A model the refetch replaced still takes
   * precedence over a stale marker.
   */
  private getLoadedModel(key: string, modelBeforeRefetch: ManagedModelTypes[T] | null): Model<T> | null {
    if (
      getLoadingState(getStore().getState().models.loadingState, this.modelType, key) === LoadingState.FAILED
    ) {
      throw new Error(`Loading failed for model ${this.modelType} with key ${key}`);
    }
    const model = this.getModel(key);
    if (model && model !== modelBeforeRefetch) {
      return model;
    }
    if (this.isModelInaccessible(key)) {
      return INACCESSIBLE;
    }
    return model;
  }

  /**
   * Returns the model, or INACCESSIBLE if only an inaccessible marker is stored, regardless of loading state.
   * The model takes precedence because inaccessible markers are kept after the model later loads.
   */
  private getStoredModel(key: string): Model<T> | null {
    const model = this.getModel(key);
    if (model) {
      return model;
    }
    return this.isModelInaccessible(key) ? INACCESSIBLE : null;
  }
}
